import React, { useEffect, useRef } from "react";
import { TableProps } from "../../../interfaces/table";
import { useStaffStore } from "../../../stores/staff";
import useModals from "../../../hooks/useModals";
import DeleteModal from "../modals/DeleteModal";
import { useUtilsStore } from "../../../stores/components/utils";
import {
  Badge,
  DesktopTable,
  IconButton,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  emptyStateIcon,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TableStaff = ({ columns, data }: TableProps) => {
  const { setIdUser, setViewOrUpdate, tabStaff, setIdEvent } = useStaffStore();
  const { searchTerm } = useUtilsStore();

  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLDivElement>,
    id: number
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (tabStaff === "Utilisateurs") {
      setIdUser(id);
    } else {
      setIdEvent(id);
    }
    setViewOrUpdate("update");
  };

  useEffect(() => {
    if (searchTerm !== "" && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const [primaryColumn, ...secondaryColumns] = columns;

  const renderDeleteButton = (id: number) => (
    <IconButton
      label="Supprimer"
      tone="danger"
      onClick={(e) => {
        e.stopPropagation();
        openModal(
          "deleteModal",
          <DeleteModal closeModal={() => closeModal("deleteModal")} id={id} />
        );
      }}
    >
      <TrashIcon />
    </IconButton>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState
            title={
              tabStaff === "Utilisateurs"
                ? "Aucun utilisateur trouvé"
                : "Aucun évènement trouvé"
            }
            description={
              tabStaff === "Utilisateurs"
                ? "Ajoutez des utilisateurs pour les voir apparaître ici."
                : "Les évènements apparaîtront ici une fois créés."
            }
            icon={emptyStateIcon(
              tabStaff === "Utilisateurs"
                ? "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            )}
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <Th key={index}>{col.header}</Th>
                  ))}
                  <Th align="right" className="w-px">
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const isInactive =
                    row.leavingDate !== null && tabStaff === "Utilisateurs";

                  return (
                    <tr
                      key={rowIndex}
                      className={rowClasses({ muted: isInactive })}
                      onClick={(e) => handleRowClick(e, row.id)}
                    >
                      {columns.map((col, colIndex) => (
                        <Td
                          key={colIndex}
                          className={
                            colIndex === 0
                              ? "font-semibold text-slate-900"
                              : isInactive
                                ? "text-slate-400"
                                : "text-slate-600"
                          }
                        >
                          {colIndex === 0 ? (
                            <div className="flex items-center gap-2">
                              <span>{renderCellValue(row[col.accessor])}</span>
                              {isInactive && <Badge>Sorti</Badge>}
                            </div>
                          ) : (
                            renderCellValue(row[col.accessor])
                          )}
                        </Td>
                      ))}
                      <Td>
                        <div className="flex justify-end">
                          {renderDeleteButton(row.id)}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const isInactive =
                  row.leavingDate !== null && tabStaff === "Utilisateurs";

                return (
                  <li key={rowIndex}>
                    <div
                      onClick={(e) => handleRowClick(e, row.id)}
                      className={cardClasses({ muted: isInactive })}
                    >
                      <div className="min-w-0">
                        <MicroLabel>{primaryColumn?.header}</MicroLabel>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <p
                            className={`text-[15px] leading-snug font-bold wrap-break-word ${
                              isInactive ? "text-slate-500" : "text-slate-900"
                            }`}
                          >
                            {primaryColumn
                              ? renderCellValue(row[primaryColumn.accessor])
                              : null}
                          </p>
                          {isInactive && <Badge>Sorti</Badge>}
                        </div>
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCellValue(row[col.accessor])}
                              muted={isInactive}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}

                      <MobileCardFooter>
                        {renderDeleteButton(row.id)}
                      </MobileCardFooter>
                    </div>
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableStaff;
